/* custom-select-search — editable, type-to-filter combobox (a "search select").
 * Distinct from custom-select (a click-only button dropdown): here the field itself is an
 * editable input, for LONG option lists (days, years, countries…). Upgrades a native
 * <select data-combo-select> inside a [data-combo-search] wrapper. The native <select> stays
 * as a11y / no-JS fallback and HOLDS THE VALUE (fires 'change'), so consumers read
 * select.value as usual. Only real options are selectable — text that doesn't match an
 * option reverts on blur.
 *
 * Usage:
 *   <div class="velara-combo" data-combo-search>
 *     <select id="birthday" data-combo-select aria-label="Day" inputmode="numeric">
 *       <option value="">Day</option>        // leading option with value ""/"0" = placeholder
 *       <option value="1">1</option> …        // (excluded from the list)
 *     </select>
 *   </div>
 *   {{ 'component-custom-select-search.css' | asset_url | stylesheet_tag }}
 *   {{ 'custom-select-search.js' | asset_url | script_tag }}
 * Keyboard: ↑/↓ move · Enter select · Esc/Tab close. Styles: component-custom-select-search.css.
 */
(function () {
  function enhance(wrap) {
    if (wrap.classList.contains('velara-combo--ready')) return;
    var sel = wrap.querySelector('[data-combo-select]');
    if (!sel) return;
    wrap.classList.add('velara-combo--ready');

    // A leading falsy-value option ("" or "0") is treated as the placeholder + excluded from the list.
    var placeholder = '', startIdx = 0;
    if (sel.options.length && (sel.options[0].value === '' || sel.options[0].value === '0')) {
      placeholder = sel.options[0].text; startIdx = 1;
    }

    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'velara-combo__input';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.autocomplete = 'off';
    if (placeholder) input.placeholder = placeholder;
    if (sel.getAttribute('aria-label')) input.setAttribute('aria-label', sel.getAttribute('aria-label'));
    if (sel.getAttribute('inputmode')) input.setAttribute('inputmode', sel.getAttribute('inputmode'));

    var caret = document.createElement('span');
    caret.className = 'velara-combo__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.textContent = '▾';

    var panel = document.createElement('ul');
    panel.className = 'velara-combo__panel';
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;

    var options = [];
    for (var i = startIdx; i < sel.options.length; i++) {
      var opt = sel.options[i];
      var li = document.createElement('li');
      li.className = 'velara-combo__option';
      li.setAttribute('role', 'option');
      li.textContent = opt.text;
      li.dataset.value = opt.value;
      var o = { li: li, value: opt.value, text: opt.text };
      li._combo = o;
      if (opt.selected && i !== startIdx) { li.setAttribute('aria-selected', 'true'); input.value = opt.text; }
      options.push(o);
      panel.appendChild(li);
    }

    var chosen = input.value || '', activeIdx = -1;
    function vis() { return options.filter(function (o) { return !o.li.hidden; }); }
    function isOpen() { return !panel.hidden; }
    function open() { panel.hidden = false; wrap.classList.add('is-open'); input.setAttribute('aria-expanded', 'true'); }
    function setActive(i) {
      var v = vis(); options.forEach(function (o) { o.li.classList.remove('is-active'); }); activeIdx = i;
      if (i >= 0 && i < v.length) { v[i].li.classList.add('is-active'); v[i].li.scrollIntoView({ block: 'nearest' }); }
    }
    function close() { panel.hidden = true; wrap.classList.remove('is-open'); input.setAttribute('aria-expanded', 'false'); setActive(-1); }
    function filter() {
      var q = input.value.trim().toLowerCase();
      options.forEach(function (o) { o.li.hidden = q !== '' && o.text.toLowerCase().indexOf(q) === -1; });
      setActive(vis().length ? 0 : -1);
    }
    function choose(o) {
      if (!o) return;
      chosen = o.text; input.value = o.text; sel.value = o.value;
      options.forEach(function (x) { if (x === o) x.li.setAttribute('aria-selected', 'true'); else x.li.removeAttribute('aria-selected'); });
      close();
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function revert() { input.value = chosen; options.forEach(function (o) { o.li.hidden = false; }); }
    function showAll() { options.forEach(function (o) { o.li.hidden = false; }); setActive(-1); }
    // Open with the FULL list — a chosen value must NOT filter the list down to itself,
    // or you can't switch. The field clears so you can pick another or type to filter, and
    // reverts to the current value on blur. The current selection is scrolled into view.
    function openFull() {
      open(); input.value = ''; showAll();
      for (var i = 0; i < options.length; i++) {
        if (options[i].li.getAttribute('aria-selected') === 'true') { options[i].li.scrollIntoView({ block: 'nearest' }); break; }
      }
    }

    input.addEventListener('focus', openFull);
    input.addEventListener('click', function () { if (!isOpen()) openFull(); });
    input.addEventListener('input', function () { if (!isOpen()) open(); filter(); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); if (!isOpen()) openFull(); var v = vis(); setActive(Math.min(activeIdx + 1, v.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(activeIdx - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); var v = vis(); if (isOpen() && activeIdx >= 0) choose(v[activeIdx]); }
      else if (e.key === 'Escape') { revert(); close(); }
      else if (e.key === 'Tab') { if (isOpen()) { revert(); close(); } }
    });
    panel.addEventListener('mousedown', function (e) { var li = e.target.closest('.velara-combo__option'); if (li) { e.preventDefault(); choose(li._combo); } });
    input.addEventListener('blur', function () { setTimeout(function () { if (isOpen()) { revert(); close(); } }, 120); });

    wrap.appendChild(input);
    wrap.appendChild(caret);
    wrap.appendChild(panel);
  }

  function init(root) { (root || document).querySelectorAll('[data-combo-search]').forEach(enhance); }

  // Rebuild a combo whose <select> was repopulated after enhancement (a province list that depends
  // on the chosen country, say). enhance() snapshots the options once, so the generated field has
  // to be thrown away rather than patched.
  function refresh(wrap) {
    if (!wrap) return;
    var made = wrap.querySelectorAll('.velara-combo__input, .velara-combo__caret, .velara-combo__panel');
    for (var i = 0; i < made.length; i++) made[i].remove();
    wrap.classList.remove('velara-combo--ready', 'is-open');
    enhance(wrap);
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.VelaraComboSearch = { init: init, refresh: refresh };
})();
